import { useMemo } from 'react'

import archidekt from '@/assets/svgs/archidekt.svg'
import cardsphere from '@/assets/svgs/cardsphere.svg'
import csb from '@/assets/svgs/commanderspellbook.svg'
import moxfield from '@/assets/svgs/moxfield.svg'
import mtgstocks from '@/assets/svgs/mtgstocks.svg'
import scryfall from '@/assets/svgs/scryfall.svg'
import { LinkOutbound } from '@/components/common/Link'
import { usePage } from '@/components/providers/PageProvider'
import { type RecType } from '@/defs/rec'
import { useCardsRec } from '@/hooks/useCardsRec'
import { cn, slugify } from '@/utils/commonUtil'
import { getRecSlug } from '@/utils/recUtil'

import styles from './FeaturedRecOutboundBar.module.scss'

type Props = {
  rec?: RecType
  variant?: 'partnerLeft' | 'partnerRight'
}

export const FeaturedRecOutboundBar = ({ rec: recProp, variant }: Props) => {
  const { page } = usePage()
  const rec = recProp || page?.featured?.rec
  const { role } = rec || {}
  const slug = rec ? getRecSlug(rec) : ''
  const { card } = useCardsRec({ rec })

  const { name, priceSources, scryfallUri } = card || {}

  const links = useMemo(
    () => [
      {
        href: `https://www.cardsphere.com/search?q=${encodeURIComponent(name || '').replace(/%20/g, '+')}`,
        label: 'Cardsphere',
        src: cardsphere.src,
      },
      {
        href: `https://archidekt.com/${role === 'commander' ? 'commanders' : 'cards'}/${encodeURIComponent(slug)}`,
        label: 'Archidekt',
        src: archidekt.src,
      },
      {
        href: `https://commanderspellbook.com/search/?q=${name}`,
        label: 'Commander Spellbook',
        src: csb.src,
      },
      {
        href: `https://moxfield.com/decks/public?format=commander&filter=${encodeURIComponent(name || '')}`,
        label: 'Moxfield',
        src: moxfield.src,
      },
      ...(scryfallUri
        ? [
            {
              href: scryfallUri,
              label: 'Scryfall',
              src: scryfall.src,
            },
          ]
        : []),
      ...(priceSources?.mtgstocks?.slug
        ? [
            {
              href: `https://www.mtgstocks.com/prints/${priceSources.mtgstocks.slug}`,
              label: 'MTG Stocks',
              src: mtgstocks.src,
            },
          ]
        : []),
    ],
    [name, priceSources, scryfallUri, role, slug],
  )

  if (!card) return null

  return (
    <div className={cn(styles.container, { styles, variant })}>
      <ul className={styles.list}>
        {links.map(({ href, label, src }, i) => (
          <li key={i}>
            <LinkOutbound
              className={cn(styles.link, { link: slugify(label), styles })}
              href={href}
              utm={{ medium: 'featured' }}
            >
              <img alt={label} decoding='async' loading='lazy' src={src} />
            </LinkOutbound>
          </li>
        ))}
      </ul>
    </div>
  )
}
